import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { connect } from 'react-redux';
import bindActions from '@/application/actions';
import { ConfirmModal } from '@components';
import * as defaultDate from '@components/CustomDatePicker/defaultValue';
import ExpenseBillForm from './components/ExpenseBillForm';
import ExpenseFilters from './components/ExpenseFilters';
import ExpenseManager from './components/ExpenseManager';
import ExpenseBillDateModal from './components/ExpenseBillDateModal';
import ExpenseBillsTable from './components/ExpenseBillsTable';
import styles from './Expense.module.scss';
import { PERMISSIONS } from '@/config/permissions';
import clsx from 'clsx';

function Expense({ data, user, actions }) {
  const [filters, setFilters] = useState(() => ({
    startDate: defaultDate.getStartDate(new Date()).getTime(),
    endDate: defaultDate.getEndDate(new Date()).getTime(),
    dateOrd: 'DESC',
    expense: '',
  }));
  const [manage, setManage] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [error, setError] = useState('');
  const loadingMore = useRef(false);
  const refresh = useCallback(
    () => actions.fetchExpenseBills({ ...filters, page: 1 }),
    [actions, filters],
  );
  useEffect(() => {
    actions.fetchExpenses();
  }, [actions]);
  useEffect(() => {
    refresh();
  }, [refresh]);

  const mutate = async (action, payload) => {
    setError('');
    try {
      await actions[action](payload).unwrap();
    } catch (e) {
      setError(
        typeof e === 'string'
          ? e
          : 'Không thể lưu thay đổi. Vui lòng thử lại.',
      );
      return false;
    }
    actions.fetchExpenses();
    refresh();
    return true;
  };
  const confirmDelete = (action, id) =>
    setConfirm({
      actionTitle: 'Xóa khoản chi?',
      actionConfirm: 'Bạn có chắc muốn xóa mục này?',
      actionConduct: () => mutate(action, { id }),
    });
  const loadMore = async () => {
    if (
      data.loading ||
      data.saving ||
      loadingMore.current ||
      !data.page ||
      data.bills.length >= data.count
    )
      return;
    loadingMore.current = true;
    try {
      await actions.fetchExpenseBills({
        ...filters,
        page: data.page + 1,
      });
    } finally {
      loadingMore.current = false;
    }
  };

  const canManageExpense = user.isAdmin;
  const expensePermission = {
    view: user.withPermission(PERMISSIONS.VIEW_QT_EXPENSE),
    edit: user.withPermission(PERMISSIONS.EDIT_QT_EXPENSE),
  };
  const expenseHistoryPermission = {
    view: user.withPermission(PERMISSIONS.VIEW_QT_EXPENSE_HISTORY),
    edit: user.withPermission(PERMISSIONS.EDIT_QT_EXPENSE_HISTORY),
  };
  const canDeleteColumn = (expenseBill) =>
    expenseHistoryPermission.edit ||
    (expenseBill.employeeID === user.id &&
      new Date(expenseBill.createdAt).toDateString() ===
        new Date().toDateString());

  return (
    <>
      <div
        className={clsx(styles.container, {
          [styles.canCreateExpenseBill]: expensePermission.edit,
        })}
      >
        <div className={styles.containerHeader}>
          {error && <p role="alert">{error}</p>}
          <ExpenseBillForm
            items={data.items}
            saving={data.saving}
            onSubmit={(value) => mutate('createExpenseBill', value)}
            onManage={() => setManage(true)}
            canCreateExpenseBill={expensePermission.edit}
            canManageExpense={canManageExpense}
          />
          <ExpenseFilters
            canViewExpenseHistory={expenseHistoryPermission.view}
            data={data}
            filters={filters}
            onChange={(patch) =>
              setFilters((value) => ({ ...value, ...patch }))
            }
            onRefresh={refresh}
          />
        </div>

        <ExpenseBillsTable
          expenseBills={data.bills}
          order={filters.dateOrd}
          onSort={() =>
            setFilters((value) => ({
              ...value,
              dateOrd: value.dateOrd === 'ASC' ? 'DESC' : 'ASC',
            }))
          }
          onEdit={setEditing}
          onDelete={(id) => confirmDelete('deleteExpenseBill', id)}
          onLoadMore={loadMore}
          saving={data.saving}
          loading={data.loading}
          hasMore={data.bills.length < data.count}
          canEditExpenseHistory={expenseHistoryPermission.edit}
          canDeleteColumn={canDeleteColumn}
        />
      </div>

      {manage && (
        <ExpenseManager
          error={error}
          items={data.items}
          saving={data.saving}
          onClose={() => setManage(false)}
          onSave={(value) =>
            mutate(
              value.id ? 'updateExpense' : 'createExpense',
              value,
            )
          }
          onDelete={(id) => confirmDelete('deleteExpense', id)}
        />
      )}
      {editing && (
        <ExpenseBillDateModal
          error={error}
          key={editing.id}
          bill={editing}
          saving={data.saving}
          onClose={() => setEditing(null)}
          onSave={(value) => mutate('updateExpenseBill', value)}
        />
      )}
      {confirm && (
        <ConfirmModal
          modal={confirm}
          show
          closeModal={() => setConfirm(null)}
        />
      )}
    </>
  );
}
export default connect(
  ({ state }) => ({ data: state.expenses, user: state.user }),
  bindActions,
)(Expense);
